/**
 * ModificationCard — Phase 6a.22 per Spec §21.7.8.2.
 *
 * One row of the modifications `useFieldArray`. The card is locally
 * collapsible (expanded by default for newly-added entries) and renders
 * a *type-aware* field set:
 *
 *   • Always: id, date, type, description
 *   • TERM_EXTENSION / TERM_REDUCTION  →  newRemainingTermMonths + newDiscountRate
 *   • PAYMENT_CHANGE                   →  newPaymentAmount + newDiscountRate
 *   • INCREASE_SCOPE_NOT_SEPARATE      →  newPaymentAmount + newDiscountRate
 *   • DECREASE_SCOPE                   →  partialTerminationPercent + newDiscountRate
 *   • INCREASE_SCOPE_SEPARATE          →  warning banner only (§44(a) carve-out)
 *
 * Conditional fields are toggled via `useWatch(modificationType)` — the
 * hidden subsidiary fields stay registered in the form so toggling the
 * type back doesn't blow away the auditor's entered values. Type-help
 * text under the Select cites the relevant IFRS 16 §44 sub-clause so
 * the auditor sees the treatment they're committing to.
 *
 * Delete action goes through a confirmation dialog (destructive action
 * rule). The dialog is owned by the parent <ModificationsSection /> so
 * cards don't each mount their own portal.
 */

import { ChevronDown, ChevronRight, Info, X } from "lucide-react";
import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Admonition } from "@/components/ui/admonition";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatIsoToDisplay } from "@/components/ui/date-picker.utils";
import { cn } from "@/lib/utils";

import {
  MODIFICATION_TYPES,
  type ContractFormValues,
} from "../schemas/contractFormSchema";
import { buildPeriodEndDates } from "../utils/buildPeriodEndDates";

import { FormMessageT } from "./FormMessageT";

const INPUT_CHROME = "bg-background border border-input shadow-sm";
const LABEL_FLEX =
  "flex items-center gap-1.5 text-sm font-medium text-foreground/90";

interface ModificationCardProps {
  index: number;
  onDelete: () => void;
}

export function ModificationCard({ index, onDelete }: ModificationCardProps) {
  const { t } = useTranslation();
  const form = useFormContext<ContractFormValues>();
  const [expanded, setExpanded] = React.useState(true);

  const modType = useWatch({
    control: form.control,
    name: `modifications.${index}.modificationType`,
  });
  const modDate = useWatch({
    control: form.control,
    name: `modifications.${index}.modificationDate`,
  });
  const commencementDate = useWatch({
    control: form.control,
    name: "commencementDate",
  });
  const leaseTermMonths = useWatch({
    control: form.control,
    name: "leaseTermMonths",
  });
  const periodEndDates = React.useMemo(
    () => buildPeriodEndDates(commencementDate, leaseTermMonths),
    [commencementDate, leaseTermMonths],
  );

  const showTermField =
    modType === "TERM_EXTENSION" || modType === "TERM_REDUCTION";
  const showPaymentField =
    modType === "PAYMENT_CHANGE" ||
    modType === "INCREASE_SCOPE_NOT_SEPARATE";
  const showPartialPercent = modType === "DECREASE_SCOPE";
  const showSeparateScopeBanner = modType === "INCREASE_SCOPE_SEPARATE";
  // §44(a) carve-out: newDiscountRate is hidden+optional for the
  // "treat as new lease" case. For every other selected type, the rate
  // is required and shown. Before the auditor picks a type at all
  // (modType is empty), keep it hidden to reduce visual noise.
  const showDiscountRate = Boolean(modType) && !showSeparateScopeBanner;

  const dateLabel = modDate
    ? formatIsoToDisplay(modDate) || modDate
    : t("engagement.contract.modifications.card.unsetDate");
  const typeLabel = modType
    ? t(`engagement.contract.modifications.types.${modType}` as never)
    : t("engagement.contract.modifications.card.unsetType");
  const headerSummary = `${t(
    "engagement.contract.modifications.card.title",
    { number: index + 1 },
  )}: ${dateLabel} · ${typeLabel}`;

  return (
    <div
      className="rounded-md border border-input bg-card"
      data-testid={`modification-card-${index}`}
    >
      <div className="flex items-center justify-between p-3 border-b">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex flex-1 items-center gap-2 text-left"
          data-testid={`modification-card-toggle-${index}`}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
          <span className="text-sm font-medium">{headerSummary}</span>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onDelete}
          aria-label={t(
            "engagement.contract.modifications.deleteConfirm.title",
          )}
          data-testid={`modification-card-delete-${index}`}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {expanded && (
        <div className="space-y-4 p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name={`modifications.${index}.modificationId`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={LABEL_FLEX}>
                    {t(
                      "engagement.contract.modifications.fields.modificationId.label",
                    )}
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={cn(INPUT_CHROME)}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "engagement.contract.modifications.fields.modificationId.helper",
                    )}
                  </p>
                  <FormMessageT />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`modifications.${index}.modificationDate`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={LABEL_FLEX}>
                    {t(
                      "engagement.contract.modifications.fields.modificationDate.label",
                    )}
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  {periodEndDates.length === 0 ? (
                    <p
                      className="text-xs italic text-muted-foreground"
                      data-testid={`modification-date-prereq-${index}`}
                    >
                      {t(
                        "engagement.contract.modifications.fields.modificationDate.prereq",
                      )}
                    </p>
                  ) : (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ""}
                    >
                      <FormControl>
                        <SelectTrigger
                          className={cn(INPUT_CHROME)}
                          data-testid={`modification-date-trigger-${index}`}
                        >
                          <SelectValue
                            placeholder={t(
                              "engagement.contract.modifications.fields.modificationDate.placeholder",
                            )}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {periodEndDates.map((d, i) => (
                          <SelectItem key={d} value={d}>
                            {`${t(
                              "engagement.contract.modifications.fields.modificationDate.optionPrefix",
                              { period: i + 1 },
                            )} ${formatIsoToDisplay(d)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "engagement.contract.modifications.fields.modificationDate.help",
                    )}
                  </p>
                  <FormMessageT />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name={`modifications.${index}.modificationType`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={LABEL_FLEX}>
                  {t(
                    "engagement.contract.modifications.fields.modificationType.label",
                  )}
                  <span className="text-destructive">*</span>
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? ""}
                >
                  <FormControl>
                    <SelectTrigger className={cn(INPUT_CHROME)}>
                      <SelectValue
                        placeholder={t(
                          "engagement.contract.modifications.fields.modificationType.placeholder",
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {MODIFICATION_TYPES.map((mt) => (
                      <SelectItem key={mt} value={mt}>
                        {t(
                          `engagement.contract.modifications.types.${mt}` as never,
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {modType && (
                  <p className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
                    <Info className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>
                      {t(
                        `engagement.contract.modifications.typeHelp.${modType}` as never,
                      )}
                    </span>
                  </p>
                )}
                <FormMessageT />
              </FormItem>
            )}
          />

          {showSeparateScopeBanner && (
            <div
              data-testid={`modification-card-separate-banner-${index}`}
            >
              <Admonition type="warning">
                {t(
                  "engagement.contract.modifications.banner.separateScope",
                )}
              </Admonition>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {showTermField && (
              <FormField
                control={form.control}
                name={`modifications.${index}.newRemainingTermMonths`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={LABEL_FLEX}>
                      {t(
                        "engagement.contract.modifications.fields.newRemainingTermMonths.label",
                      )}
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={600}
                        step={1}
                        inputMode="numeric"
                        {...field}
                        value={field.value ?? ""}
                        className={cn(
                          INPUT_CHROME,
                          "text-right font-mono tabular-nums",
                        )}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "engagement.contract.modifications.fields.newRemainingTermMonths.helper",
                      )}
                    </p>
                    <FormMessageT />
                  </FormItem>
                )}
              />
            )}

            {showPaymentField && (
              <FormField
                control={form.control}
                name={`modifications.${index}.newPaymentAmount`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={LABEL_FLEX}>
                      {t(
                        "engagement.contract.modifications.fields.newPaymentAmount.label",
                      )}
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0.01}
                        step={0.01}
                        inputMode="decimal"
                        {...field}
                        value={field.value ?? ""}
                        className={cn(
                          INPUT_CHROME,
                          "text-right font-mono tabular-nums",
                        )}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "engagement.contract.modifications.fields.newPaymentAmount.helper",
                      )}
                    </p>
                    <FormMessageT />
                  </FormItem>
                )}
              />
            )}

            {showPartialPercent && (
              <FormField
                control={form.control}
                name={`modifications.${index}.partialTerminationPercent`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={LABEL_FLEX}>
                      {t(
                        "engagement.contract.modifications.fields.partialTerminationPercent.label",
                      )}
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type="number"
                          min={0.01}
                          max={99.99}
                          step={0.01}
                          inputMode="decimal"
                          {...field}
                          value={field.value ?? ""}
                          className={cn(
                            INPUT_CHROME,
                            "pr-8 text-right font-mono tabular-nums",
                          )}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          %
                        </span>
                      </div>
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "engagement.contract.modifications.fields.partialTerminationPercent.helper",
                      )}
                    </p>
                    <FormMessageT />
                  </FormItem>
                )}
              />
            )}

            {showDiscountRate && (
              <FormField
                control={form.control}
                name={`modifications.${index}.newDiscountRate`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={LABEL_FLEX}>
                      {t(
                        "engagement.contract.modifications.fields.newDiscountRate.label",
                      )}
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type="number"
                          min={0.01}
                          max={500}
                          step={0.01}
                          inputMode="decimal"
                          {...field}
                          value={field.value ?? ""}
                          className={cn(
                            INPUT_CHROME,
                            "pr-8 text-right font-mono tabular-nums",
                          )}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          %
                        </span>
                      </div>
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "engagement.contract.modifications.fields.newDiscountRate.helper",
                      )}
                    </p>
                    <FormMessageT />
                  </FormItem>
                )}
              />
            )}
          </div>

          <FormField
            control={form.control}
            name={`modifications.${index}.description`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={LABEL_FLEX}>
                  {t(
                    "engagement.contract.modifications.fields.description.label",
                  )}
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ""}
                    maxLength={500}
                    rows={2}
                    className={cn(INPUT_CHROME)}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "engagement.contract.modifications.fields.description.helper",
                  )}
                </p>
                <FormMessageT />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}
